import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, FormInputText, Link, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { OpenInNewRounded, SecurityRounded } from '@mui/icons-material';
import { Button } from '@rideiq/shared/components/Button';
import * as yup from 'yup';

import {
  analyticsMFACloseButton,
  analyticsMFAConfirmButton,
  analyticsMFALearnMore,
  analyticsMFAResend,
  analyticsPage,
} from '../../../../api/services/Analytics';
import { colorTokens } from '../../../../shared/components/variables';
import { MFAMethodOption } from '../../../types';

import {
  CloseButton,
  FormMessage,
  MethodNavigation,
  MFAModalPaper,
  ModalActionsStyled,
  SecondaryButtonStyled,
} from './MultiFactorAuth.styles';

export interface MultiFactorAuthProps {
  serverErrors?: string[];
  showMFA: boolean;
  setShowMFA: Dispatch<SetStateAction<boolean>>;
  isLocked: boolean;
  isVerifying?: boolean;
  handleMFAVerify: (code: string) => void;
  resendMFACode: () => Promise<boolean>;
  deliveryErrors?: string[];
  userEmail: string;
  delivery?: MFAMethodOption;
  onChooseMethod?: () => void;
  sendCooldownSeconds?: number;
  codeSent?: boolean;
  isDeliveryUnavailable?: boolean;
}

type FormData = {
  mfaCode: string;
};

const validationSchema = yup.object().shape({
  mfaCode: yup.string().length(6, 'MFA Code must be exactly six characters long.').matches(/^\d+$/),
});

export const MultiFactorAuth: FC<MultiFactorAuthProps> = ({
  serverErrors,
  showMFA,
  setShowMFA,
  isLocked,
  isVerifying = false,
  handleMFAVerify,
  resendMFACode,
  userEmail,
  delivery,
  deliveryErrors,
  onChooseMethod,
  sendCooldownSeconds = 0,
  codeSent = false,
  isDeliveryUnavailable = false,
}) => {
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    getValues,
    setFocus,
    formState: { isDirty, isValid, errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: { mfaCode: '' },
  });
  const channel = delivery?.method ?? 'email';
  const resendDisabled = sendCooldownSeconds > 0;
  const [resending, setResending] = useState(false);
  const resendPending = useRef(false);
  const generation = useRef(0);
  const dismissed = useRef(false);
  const resendLabel = resendDisabled && codeSent ? 'Code sent' : 'Send again';
  const mfaHelpUrl = window.env?.RIDEIQ_MFA_HELP_URL;

  const dismissMFA = () => {
    if (!showMFA || dismissed.current) {
      return;
    }
    dismissed.current = true;
    setShowMFA(false);
    analyticsMFACloseButton(userEmail, !!getValues('mfaCode'), isLocked, channel);
  };

  const submit = (data: FormData, e) => {
    e.preventDefault();
    if (isVerifying || resending || isLocked) {
      return;
    }
    clearErrors();
    handleMFAVerify(data.mfaCode);
    analyticsMFAConfirmButton(userEmail, !!getValues('mfaCode'), channel);
  };

  const onResendClick = async () => {
    if (resendPending.current || isVerifying || isLocked || isDeliveryUnavailable) {
      return;
    }
    analyticsMFAResend(userEmail, !!getValues('mfaCode'), resendDisabled, channel);
    if (resendDisabled) {
      return;
    }
    const attempt = generation.current;

    resendPending.current = true;
    setResending(true);
    clearErrors('root.resend');
    try {
      const sent = await resendMFACode();

      if (generation.current === attempt && sent) {
        reset();
      }
    } catch {
      if (generation.current === attempt) {
        setError('root.resend', { message: 'Unable to send a verification code. Please try again.' });
      }
    } finally {
      if (generation.current === attempt) {
        resendPending.current = false;
        setResending(false);
      }
    }
  };

  useEffect(() => {
    reset();
  }, [delivery?.method, delivery?.destination, isLocked, reset]);

  useEffect(() => {
    clearErrors('mfaCode');
    clearErrors('root');
    if (serverErrors?.length) {
      setError(isLocked ? 'root' : 'mfaCode', { message: serverErrors.join(' ') });
      if (!isLocked) {
        setFocus('mfaCode');
      }
    }
  }, [serverErrors, setError, clearErrors, setFocus, isLocked]);

  useEffect(() => {
    if (!showMFA) {
      reset();
      setResending(false);
      resendPending.current = false;
    } else {
      dismissed.current = false;
      analyticsPage('MFA Modal', { email: userEmail, mfa_channel: channel });
    }

    return () => {
      generation.current += 1;
    };
  }, [showMFA, reset, userEmail]);

  return (
    <Modal
      open={showMFA}
      maxWidth="md"
      data-testid="mfa-modal"
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          dismissMFA();
        }
      }}
      disableRestoreFocus={false}
      aria-labelledby="mfa-code-title"
      aria-describedby={isLocked ? 'mfa-lock-description' : 'mfa-code-description'}
      PaperComponent={MFAModalPaper}
      PaperProps={{ style: { borderRadius: 8 } }}
    >
      <Modal.Title id="mfa-code-title" display="flex" alignItems="center">
        Multi Factor Authentication
        <CloseButton type="button" aria-label="Close verification" onClick={dismissMFA} data-testid="modal-close-icon">
          <Svg height="20px" icon="close" />
        </CloseButton>
      </Modal.Title>
      <form aria-busy={isVerifying} className="form" onSubmit={handleSubmit(submit)}>
        {isLocked ? (
          <Modal.Content id="mfa-lock-description">
            <Alert severity="error" message={errors.root?.message || ''} />
          </Modal.Content>
        ) : (
          <Modal.Content>
            {deliveryErrors?.map((error) => (
              <Alert key={error} severity="error" message={error} />
            ))}
            <Text id="mfa-code-description" variant="bodyS">
              {delivery
                ? `Multi-Factor Authentication (MFA) is required for added protection. Enter the 6-digit code we sent to ${delivery.destination}.`
                : 'Multi-Factor Authentication (MFA) is now required for added protection. Check your email inbox for the 6-digit code.'}
            </Text>

            <Box display="flex" marginTop="24px">
              <Controller
                name="mfaCode"
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <FormInputText
                    id="mfaCode"
                    value={value}
                    inputRef={ref}
                    onBlur={onBlur}
                    disabled={isVerifying}
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                    inputProps={{
                      'data-testid': 'mfaCode',
                      'aria-label': '6-digit code',
                      inputMode: 'numeric',
                      maxLength: 6,
                    }}
                    variant="outlined"
                    icon={<SecurityRounded />}
                    error={!!errors.mfaCode}
                    autoFocus
                    fullWidth
                    helperText={errors.mfaCode?.message || ''}
                    FormHelperTextProps={{ role: 'alert' }}
                    onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  />
                )}
              />
              <SecondaryButtonStyled
                color="secondary"
                size="medium"
                data-testid="mfa-resend-button"
                className={resendDisabled ? 'is-disabled' : ''}
                onClick={onResendClick}
                disabled={resending || isVerifying || resendDisabled || isDeliveryUnavailable}
                aria-busy={resending}
              >
                {resending ? 'Sending…' : resendLabel}
              </SecondaryButtonStyled>
            </Box>
            {resendDisabled && (
              <Text role="status" variant="bodyS">
                Please wait before requesting another code.
              </Text>
            )}
            {isVerifying && (
              <Text role="status" variant="bodyS">
                Verifying code…
              </Text>
            )}
            {onChooseMethod && (
              <Box mt="16px" pt="16px" borderTop={`1px solid ${colorTokens.border.subtle}`}>
                <Text variant="bodyS">
                  {delivery?.method === 'sms'
                    ? 'Choose a different method? '
                    : "Don't have access to this email right now? "}
                  <MethodNavigation
                    type="button"
                    disabled={resending || isVerifying}
                    onClick={onChooseMethod}
                    data-testid="mfa-choose-method"
                  >
                    {delivery?.method === 'sms' ? 'Back' : 'Choose a different method.'}
                  </MethodNavigation>
                </Text>
              </Box>
            )}
            {errors.root?.resend && <FormMessage role="alert">{errors.root?.resend.message}</FormMessage>}
          </Modal.Content>
        )}
        <ModalActionsStyled>
          <Link
            href={mfaHelpUrl}
            underline="none"
            target="_blank"
            onClick={() => analyticsMFALearnMore(userEmail, !!getValues('mfaCode'))}
          >
            <Box display="flex" alignItems="center">
              <Text variant="labelM" textColor="secondary" mr="8px">
                Learn More about MFA
              </Text>
              <OpenInNewRounded color="primary" fontSize="small" />
            </Box>
          </Link>
          {isLocked ? (
            <Button color="primary" onClick={dismissMFA} data-testid="closeButton">
              Close
            </Button>
          ) : (
            <Button
              color="primary"
              type="submit"
              data-testid="mfa-confirm-button"
              disabled={!isDirty || !isValid || resending || isVerifying}
            >
              Confirm
            </Button>
          )}
        </ModalActionsStyled>
      </form>
    </Modal>
  );
};
